import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  ButtonBase,
  InputAdornment,
  Link,
  TextField,
} from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { isEqual, omit } from "lodash";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomDataGridV2 from "../../../../components/CustomDataGridV2";
import CustomSelect from "../../../../components/CustomSelect";
import Flex from "../../../../components/Flex";
import LabeledSwitch from "../../../../components/LabeledSwitch";
import MaskMultiSelect from "../../../../components/MaskMultiSelect";
import MultiSpecialty from "../../../../components/MultiSpecialty";
import RenderWithTooltip from "../../../../components/RenderWithTooltip";
import Text from "../../../../components/Text";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import ActionMenu from "../../../../components/app/ActionMenu";
import CustomTitle from "../../../../components/app/CustomTitle";
import DeleteModal from "../../../../components/app/DeleteModal";
import StyledButtonV2 from "../../../../components/app/StyledButtonV2";
import ProviderGroupDrawer from "../../../../components/app/admin/provider-groups/ProviderGroupDrawer";
import {
  STATE_LIST,
  STATE_RECORD_FULL_NAME_MAPPER,
} from "../../../../constants";
import { STATUS } from "../../../../constants/AutocompleteOptions";
import {
  ProviderGroup,
  ProviderGroupSubmitValues,
} from "../../../../constants/ProviderGroupFormInitialValues";
import { ProviderInfoType } from "../../../../dummy-data/providerInfo";
import { useDebounce } from "../../../../hooks/use-debounce";
import { useReduceDisclosure } from "../../../../hooks/use-reduce-disclosure";
import { useArchiveState } from "../../../../hooks/useArchiveState";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { useAddProviderGroup } from "../../../../queries/admin/provider-group/use-add-provider-group";
import { useEditProviderGroupProfile } from "../../../../queries/admin/provider-group/use-edit-provider-group-profile";
import { useProviderGroupProfile } from "../../../../queries/admin/provider-group/use-provider-group-profile";
import { useSendInvite } from "../../../../queries/script-sure/use-send-invite";
import { useArchiveProviderGroup } from "../../../../queries/use-archieve-provider-group";
import { useChangeProviderGroupAvatar } from "../../../../queries/use-change-avatar";
import { useProviderGroupList } from "../../../../queries/use-get-provider-groups";
import { useStatusProviderGroup } from "../../../../queries/use-status-provider-group";
import {
  applyDefaultColumnSettings,
  DEFAULT_FONTSIZE,
  DEFAULT_INS_LABEL_LIST,
  DEFAULT_PAGE_SIZE,
  DEFAULT_UUID_NAME_LIST,
  DEFAULT_VALUE_LABEL_LIST,
  DGT_Pagination,
  formatPhoneNumber,
  getAddressFormat,
  getUnNecessaryTransformData,
  getXtenantId,
  showToastOnApiCall,
  transformMultiSelectFrontEndVal,
  unNecessaryFEOfficeHourTrans,
} from "../../../../utils/utils";

const ProviderGroupsLayout = () => {
  const [addProviderGroupDrawerDisclosure, editProviderUserModalDisclosure] =
    useReduceDisclosure(2);
  const [providerGroupUUID, setProviderGroupUUID] = useState<
    string | undefined
  >(undefined);
  const { data: profileData }: any = useProviderGroupProfile(providerGroupUUID);

  const [isArchieved, setIsArchieved] = useState(false);
  const [statusFilter, setStatusFilter] = useState<boolean | string>("all");
  const [stateFilter, setStateFilter] =
    useState<typeof DEFAULT_VALUE_LABEL_LIST>();
  const [searchKey, setSearchKey, instantSearchVal] = useDebounce<string>("");
  const { mutateAsync: achieveProviderGroup } = useArchiveProviderGroup();
  const { deleteDisc: deleteDisClosure, archiveHandler } =
    useArchiveState(achieveProviderGroup);
  const [pagination, setPagination] = useState<DGT_Pagination>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { mutateAsync: sendInvite } = useSendInvite();
  const {
    data: providerGroupList = { count: 0, content: [] },
    isLoading,
    refetch,
  } = useProviderGroupList({
    pagination,
    status:
      statusFilter === "all"
        ? undefined
        : typeof statusFilter === "boolean"
          ? statusFilter
          : undefined,
    state: stateFilter?.length
      ? stateFilter?.map((it) => it?.value)?.join(",")
      : undefined,
    gridSorting,
    searchKey: searchKey,
    archieved: isArchieved,
  });
  const navigate = useNavigate();
  const { mutateAsync: ChangeProviderGroupAvatar } =
    useChangeProviderGroupAvatar();
  const { mutateAsync: editProviderGroupProfile } =
    useEditProviderGroupProfile();
  const { mutateAsync: addProviderGroup } = useAddProviderGroup();
  const { mutateAsync: statusChange } = useStatusProviderGroup();
  // const { mutateAsync: sendScriptsureInvitation } = useSendScriptsureInvitation();
  const showSnackbar = useSnackbar();

  const sendInviteHandler = (row: ProviderInfoType) => {
    showToastOnApiCall({
      apiCall: sendInvite({
        providerGroupId: row?.uuid,
        xTENANTID: getXtenantId(),
      }),
      showSnackbar,
    });
  };

  const COLUMNS = applyDefaultColumnSettings<
    ProviderInfoType & { archive?: boolean }
  >([
    {
      field: "name",
      flex: 0.6,
      headerName: "Name",
      minWidth: 220,
      disableReorder: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => {
        return (
          <ButtonBase
            onClick={() =>
              navigate(`/app/provider-groups/${row.schema}/${row.uuid}`)
            }
          >
            <Text c={"primary.main"}>{row.name}</Text>
          </ButtonBase>
        );
      },
    },
    {
      field: "specialities",
      flex: 0.6,
      minWidth: 140,
      headerName: "Speciality",
      sortable: false,
      renderCell: ({ value }) => (
        <MultiSpecialty specialties={value} width={280} />
      ),
    },
    {
      field: "phone",
      headerName: "Contact",
      flex: 0.5,
      minWidth: 200,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Text>{row?.phone ? formatPhoneNumber(row?.phone) : "-"}</Text>
      ),
    },
    {
      field: "website",
      headerName: "Website",
      flex: 0.5,
      minWidth: 200,
      disableColumnMenu: true,
      renderCell: ({ row }) => {
        const formatLink = row?.website?.includes?.("https")
          ? row?.website
          : "https://" + row?.website;

        return (
          <Link href={formatLink} underline="none" target="_blank">
            <Text variant="body2">{row?.website ? row?.website : "-"}</Text>
          </Link>
        );
      },
    },

    {
      field: "physicalAddressLine",
      headerName: "Address",
      flex: 0.5,
      minWidth: 200,
      disableColumnMenu: true,
      renderCell: ({ row }) => {
        const address = getAddressFormat(row?.physicalAddress);

        return <RenderWithTooltip value={address} />;
      },
    },
    {
      field: "physicalAddressState",
      headerName: "State",
      width: 150,
      disableColumnMenu: true,
      renderCell: ({ row }) => {
        return (
          <Text>
            {row?.physicalAddress?.state
              ? STATE_RECORD_FULL_NAME_MAPPER[row.physicalAddress.state]
              : "-"}
          </Text>
        );
      },
    },
    {
      field: "active",
      headerName: "Status",
      width: 150,
      disableColumnMenu: true,

      renderCell: ({ row, value }) => (
        <ToggleSwitch
          content={`Are you sure you want to ${
            value ? "deactivate" : "activate"
          } this Provider Group? ${!value ? "Enabling" : "Disabling"} it will ${
            !value ? "allow" : "prevent"
          } all associated Providers and Staff users ${
            !value ? "to log in to" : "from logging in to"
          } the Provider Portal.`}
          confirmButtonText={value ? "Yes, Deactivate" : "Yes, Activate"}
          id={row.uuid.toString()}
          checked={value}
          readOnly={row?.archive}
          onConfirm={(v, id) => toggleStatus(v, id)}
        />
      ),
    },
    {
      field: "Action",
      width: 90,
      hideSortIcons: true,
      renderCell: ({ row }: any) => {
        return (
          <ActionMenu
            options={[
              {
                value: "edit",
                label: "Edit",
                isDisabled: isArchieved ? false : !row?.active,
                hoverMsg: "Enable Status For Edit",
              },
              { value: "sendInvitation", label: "Send scriptsure Invitation" },
              {
                value: "archive",
                label: row?.archive ? "Unarchive" : "Archive",
                isDisabled: isArchieved ? false : row.active || row?.archive,
                hoverMsg: "Make status disable for archive",
              },
            ].filter((it) =>
              isArchieved
                ? !["edit", "sendInvitation"].includes(it.value)
                : true
            )}
            onActionClick={(v) => {
              if (v === "sendInvitation") {
                sendInviteHandler(row);
              } else if (v === "edit") {
                handleOpenEditProfileModal(row as any);
              } else if (v === "archive") {
                deleteDisClosure.open(row?.uuid);
              }
            }}
          />
        );
      },
    },
  ]);

  function toggleStatus<T>(value: boolean, id: T) {
    return statusChange({ depId: id?.toString() ?? "", status: value }).then(
      (res) => {
        refetch();
        return res;
      }
    );
  }

  function handleOpenEditProfileModal(user: ProviderGroup) {
    editProviderUserModalDisclosure.open();
    setProviderGroupUUID(user.uuid!);
  }

  function onAddProviderGroupSubmit(values: ProviderGroupSubmitValues) {
    return addProviderGroup({
      providerGroup: {
        ...getUnNecessaryTransformData(
          ["specialities", "acceptedInsurances"],
          values
        ),
        uuid: values?.uuid,
      },
    });
  }

  function onEditSubmit(values: ProviderGroupSubmitValues) {
    const Transformed = {
      ...profileData,
      officeHours: unNecessaryFEOfficeHourTrans(profileData?.officeHours),
      acceptedInsurances: transformMultiSelectFrontEndVal(
        DEFAULT_INS_LABEL_LIST,
        profileData?.acceptedInsurances as any
      ),
      specialities: transformMultiSelectFrontEndVal(
        DEFAULT_UUID_NAME_LIST,
        profileData?.specialities as any
      ),
    };

    return editProviderGroupProfile({
      providerGroup: {
        ...getUnNecessaryTransformData(
          ["specialities", "acceptedInsurances"],
          values
        ),
        uuid: values?.uuid,
      },
    }).then((res) => {
      if (!values?.avatar?.includes(".amazonaws.com")) {
        return ChangeProviderGroupAvatar({
          providerGroupId: values?.uuid ?? "",
          avatar: {
            newAvatar: values.avatar,
          },
        }).then((avatarResp) => {
          if (isEqual(omit(values, "avatar"), omit(Transformed, "avatar"))) {
            return avatarResp;
          } else return res;
        });
      }
      refetch();
      return res;
    });
  }

  return (
    <Box height={"100%"} width={"100%"} minHeight={"calc(100vh - 10.5rem)"}>
      <DeleteModal
        w="380px"
        open={deleteDisClosure.opened}
        onClose={deleteDisClosure.close}
        id={deleteDisClosure?.deleteId}
        onConfirmation={(id) =>
          archiveHandler(!isArchieved, id as string).then((res) => {
            refetch();
            return res;
          })
        }
        content={`You want to ${
          profileData?.archive ? "Unarchive" : "Archive"
        } this Provider Group`}
      />

      <Flex justifyContent={"space-between"} alignItems={"flex-start"}>
        <CustomTitle>Provider Groups</CustomTitle>
        <Flex alignItems={"center"} gap={"10px"}>
          <LabeledSwitch
            label="Show Archived"
            checked={isArchieved}
            onChange={(e) => setIsArchieved(e.target.checked)}
          />
          <TextField
            value={instantSearchVal}
            sx={{ width: "16rem", "& .MuiInputBase-input": DEFAULT_FONTSIZE }}
            placeholder="Search By Name"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={(e) => {
              setSearchKey(e.currentTarget.value);
            }}
          />
          <Box width={"8rem"}>
            <CustomSelect
              value={statusFilter as any}
              disableSortOptions
              placeholder="Status"
              options={STATUS as any}
              onChange={(v) => setStatusFilter(v as any)}
            />
          </Box>
          <Box width={"13rem"} component={"form"} autoComplete="off">
            <MaskMultiSelect
              placeholder="States"
              value={stateFilter as any}
              optionWithCheckbox
              options={STATE_LIST}
              onChange={(val) => setStateFilter(val)}
            />
          </Box>
          <StyledButtonV2
            shouldAddIcon
            onClick={() => {
              addProviderGroupDrawerDisclosure.open();
            }}
            label="Add Provider Group"
          />
        </Flex>

        <ProviderGroupDrawer
          openProviderForm={addProviderGroupDrawerDisclosure.opened}
          handleClose={addProviderGroupDrawerDisclosure.close}
          title="Add Provider Group"
          onSubmit={onAddProviderGroupSubmit}
        />

        <ProviderGroupDrawer
          openProviderForm={editProviderUserModalDisclosure.opened}
          handleClose={editProviderUserModalDisclosure.close}
          prefillValues={
            {
              ...profileData,
              officeHours: unNecessaryFEOfficeHourTrans(
                profileData?.officeHours
              ),
              acceptedInsurances: transformMultiSelectFrontEndVal(
                DEFAULT_INS_LABEL_LIST,
                profileData?.acceptedInsurances as any
              ),
              specialities: transformMultiSelectFrontEndVal(
                DEFAULT_UUID_NAME_LIST,
                profileData?.specialities as any
              ),
            } as any
          }
          title="Edit Provider Group"
          onSubmit={onEditSubmit}
        />
      </Flex>
      {/* <DeleteModal
        w="20vw"
        open={openedDeleteModal}
        onClose={closeDeleteModal}
        id={deleteId}
        onConfirmation={(id) => {
          closeDeleteModal();
        }}
        content={"You want to delete this provider group"}
      /> */}
      <Box mt={"16px"} height={"calc(100vh - 11.5rem)"}>
        <CustomDataGridV2
          onSortModelChange={setGridSorting}
          searchString={searchKey}
          loading={isLoading}
          getRowId={(row) => row.uuid}
          rows={providerGroupList.content ?? []}
          columns={COLUMNS}
          disableRowSelectionOnClick
          rowCount={(providerGroupList as any)?.page?.totalElements}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
        />
      </Box>
    </Box>
  );
};

export default ProviderGroupsLayout;
